import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Merge the incoming request's query string into the destination.
// If the destination already has a query, we append with '&'; otherwise '?'.
const mergeQuery = (destination, incomingSearch) => {
  if (!incomingSearch || incomingSearch === "?") return destination;
  const q = incomingSearch.startsWith("?") ? incomingSearch.slice(1) : incomingSearch;
  return destination.includes("?") ? `${destination}&${q}` : `${destination}?${q}`;
};

// Catch-all route resolver: on every unknown path, ask the API whether a
// database-driven redirect exists (exact match, case-insensitive) and follow
// it if so. Real React Router routes always match first — this component only
// runs when nothing else did.
export default function RedirectResolver() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("looking-up"); // looking-up | not-found

  // The SPA answers unknown routes with HTTP 200 + this page, so mark the
  // rendered 404 noindex,nofollow. Reuse any existing robots meta (e.g. the
  // preview-domain noindex added by seo.js) instead of appending a new one.
  useEffect(() => {
    if (status !== "not-found") return undefined;
    const existing = document.head.querySelector('meta[name="robots"]');
    const meta = existing || document.createElement("meta");
    const previous = existing ? existing.getAttribute("content") : null;
    meta.setAttribute("name", "robots");
    meta.setAttribute("content", "noindex, nofollow");
    if (!existing) document.head.appendChild(meta);
    return () => {
      if (existing) {
        if (previous !== null) meta.setAttribute("content", previous);
        else meta.removeAttribute("content");
      } else {
        meta.remove();
      }
    };
  }, [status]);

  useEffect(() => {
    let cancelled = false;
    const path = location.pathname.toLowerCase();
    axios
      .get(`${API}/redirects/lookup`, { params: { path } })
      .then((res) => {
        if (cancelled) return;
        const { destination } = res.data;
        const target = mergeQuery(destination, location.search);
        if (/^https?:\/\//i.test(destination)) {
          window.location.replace(target);
        } else {
          navigate(target, { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("not-found");
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  if (status === "looking-up") return null;
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6" data-testid="page-not-found">
      <h1 className="font-display text-5xl md:text-6xl">404</h1>
      <p className="mt-4 text-neutral-500">This page doesn&rsquo;t exist.</p>
      <a href="/" className="mt-8 label-mono border border-hairline px-4 py-2 hover:bg-ink hover:text-white transition-colors">
        Go home
      </a>
    </div>
  );
}
